import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { MaskLines } from "../site/Reveal";
import { Emblem3D } from "../site/Emblem3D";
import { EASE, INTRO_DELAY } from "../../lib/api";

const fade = (d) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1, ease: EASE, delay: INTRO_DELAY + d } });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-35%"]);
  const fadeOut = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-night text-white">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <motion.img
          src="/img/look-black.webp"
          alt="MARKHOR belt bags in Black worn crossbody and at the waist"
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.6, ease: EASE, delay: Math.max(0, INTRO_DELAY - 0.4) }}
          className="h-full w-full object-cover object-[62%_center]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/5" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 to-transparent" />

      <motion.div style={{ y: textY, opacity: fadeOut }} className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-end px-5 pb-14 md:px-10 md:pb-20">
        <motion.p {...fade(0)} className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D8DCE0]">Collection 01 — The Ascent Athletic Belt Bag</motion.p>
        <MaskLines
          as="h1"
          delay={INTRO_DELAY}
          lines={["Move freely.", <em key="b" className="font-normal">Live boldly.</em>]}
          className="mt-5 font-display text-[16vw] font-light uppercase leading-[0.84] tracking-[-0.02em] sm:text-[12vw] lg:text-[9.4vw] 2xl:text-[150px]"
        />
        <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.p {...fade(0.5)} className="max-w-md text-sm leading-relaxed text-[#E4E4E6] md:text-base">
            A compact everyday belt bag in six signature colors, finished with our silver metal emblem. Built for the way you move.
          </motion.p>
          <motion.div {...fade(0.65)} className="flex flex-wrap gap-3">
            <Link to="/shop" data-testid="hero-shop-now-button" className="btn-light group">Shop now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            <a href="#collection" data-testid="hero-explore-collection-button" className="btn-ghost-light">Explore collection</a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div {...fade(0.3)} className="absolute right-5 top-[132px] z-10 w-20 sm:w-28 md:right-10 md:top-[150px] lg:w-40">
        <Emblem3D trackWindow testid="hero-emblem-3d" />
      </motion.div>
      <motion.a href="#collection" {...fade(0.9)} aria-label="Scroll down" data-testid="hero-scroll-indicator"
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#D8DCE0] md:flex">
        Scroll <ArrowDown className="h-3 w-3 animate-bounce" />
      </motion.a>
    </section>
  );
};
